import { html, type TemplateResult } from 'lit-html'
import { GAME_CONFIG, RELIC_GLYPH, type api } from '@guild/core'

import type { App } from '../app.ts'
import { defineScreen } from '../screen.ts'
import {
  dayLabel,
  errorText,
  fragmentText,
  islandName,
  manuscriptSpans,
  relicName,
  sealText,
} from '../text.ts'

/**
 * The Atlas (#57).
 *
 * Everything on it is the player's own: islands they charted, relics they won,
 * fragments they hold, the spans of the manuscript they can read, and their
 * seal. Nobody sees anybody else's and there is no leaderboard (Q5.4).
 *
 * The seal is the only place reputation is ever shown, and it is shown as one
 * of four states and a sentence, never as a score or as the counts behind one
 * (Q5.2, Q7.4).
 *
 * A span the player has not unlocked is drawn as a redaction sized from its
 * length, so the shape of the account is visible and its words are not. The
 * length is capped while a span is locked (#125): at full length an account
 * nobody has read yet is forty rows of blocks and nothing else on the page.
 */

/**
 * How wide a locked span is drawn, in ems, and the class that draws it (#125).
 *
 * From the span's own length, so the shape of the account still shows -- and
 * capped, because the manuscript's twelve spans are 1,345 characters between
 * them and a redaction at full length made forty rows of blocks that swallowed
 * the page. A tenth of the length puts the longest at fourteen ems, and the
 * spans grow to their real length as they unlock, which is where the length
 * starts to matter.
 *
 * A class rather than a `style` attribute, for the same reason `columnsClass`
 * is one: the policy is `style-src 'self'` (Q22.1, Q23.1). `app.css` carries
 * one rule for every width between the two ends.
 */
const REDACTION_MIN_EM = 3
const REDACTION_MAX_EM = 14

export const redactionClass = (length: number): string =>
  `redact-${Math.max(REDACTION_MIN_EM, Math.min(Math.round(length / 10), REDACTION_MAX_EM))}`

type State = {
  atlas: api.ApiAtlas | null
  problem: string | null
  loaded: boolean
}

const percent = (app: App, share: number): string =>
  new Intl.NumberFormat(app.locale, { style: 'percent' }).format(share)

export const atlasScreen = defineScreen<State>({
  id: 'atlas',
  initial: { atlas: null, problem: null, loaded: false },

  async enter(app, _route, store, current) {
    store.set({ problem: null })
    const result = await app.api.atlas()
    if (!current()) return
    if (!result.ok) {
      if (result.status === 401) await app.setSession(null)
      if (current()) store.set({ loaded: true, problem: errorText(app, result) })
      return
    }
    store.set({ loaded: true, atlas: result.value })
  },

  render(state, app) {
    if (state.atlas === null) {
      return html`<section class="screen">
        ${state.problem === null ? null : html`<p class="problem" role="alert">${state.problem}</p>`}
      </section>`
    }
    const atlas = state.atlas

    return html`<section class="screen screen-atlas">
      <header class="atlas-seal">
        <div class="seal seal-large seal-${atlas.seal}" aria-hidden="true">✦</div>
        <div>
          <h1 class="title">${app.t('sealHeading')}</h1>
          <p class="meta">${sealText(app, atlas.seal)}</p>
        </div>
      </header>

      <section class="atlas-column">
        <p class="label">${app.t('atlasIslands')}</p>
        ${
          atlas.islands.length === 0
            ? html`<p class="note">${app.t('atlasEmpty')}</p>`
            : html`<div class="thumbs">
                ${atlas.islands.map((island) => thumbnail(app, island))}
              </div>`
        }
      </section>

      <section class="atlas-column">
        <p class="label">${app.t('atlasRelics')}</p>
        <ul class="relic-list">
          ${atlas.relics.map(
            (relic) =>
              html`<li>
                <span class="glyph accent">${RELIC_GLYPH}</span>
                <span>${relicName(app, relic.relicId)}</span>
                <span class="meta">${islandName(app, relic.islandName)}</span>
              </li>`,
          )}
        </ul>

        <p class="label">
          ${app.t('atlasFragments')} ·
          ${app.t('atlasFragmentsCount', {
            n: atlas.fragmentIds.length,
            total: GAME_CONFIG.lore.totalFragments,
          })}
        </p>
        <ul class="fragment-list">
          ${atlas.fragmentIds.map(
            (id) => html`<li class="serif-note">${fragmentText(app, id)}</li>`,
          )}
        </ul>

        ${manuscript(app, atlas)}
      </section>
    </section>`
  },
})

/**
 * One island: its thumbnail, its name, what is charted and what the day itself
 * ended at.
 *
 * `completion` counts everything, replay included, which is what replay is for;
 * `liveCompletion` is the figure frozen at finalisation and the one the share
 * card quotes (`docs/rules.md` section 8).
 */
function thumbnail(app: App, island: api.ApiAtlas['islands'][number]): TemplateResult {
  return html`<button
    class="thumb-holder"
    @click=${() => app.go({ screen: 'island', day: island.day })}
  >
    <span class="thumb" aria-hidden="true">
      ${[...island.thumbnail].map((glyph) => html`<span>${glyph}</span>`)}
    </span>
    <span class="thumb-name">${islandName(app, island.islandName)}</span>
    <span class="meta">${dayLabel(app, island.dayNumber)}</span>
    <span class="meta">
      ${app.t('chartedLabel')} ${percent(app, island.completion)}
      ${
        island.liveCompletion === null
          ? null
          : html`<span class="quiet-ink"
              >${app.t('atlasLiveCompletion', { n: percent(app, island.liveCompletion) })}</span
            >`
      }
    </span>
  </button>`
}

/** The keeper's account, with everything not yet legible struck out. */
function manuscript(app: App, atlas: api.ApiAtlas): TemplateResult {
  const spans = manuscriptSpans(app)
  const legible = new Set(atlas.manuscriptSpanIds)
  return html`<section>
    <p class="label">
      ${app.t('atlasManuscript')} ·
      ${app.t('atlasManuscriptCount', { n: legible.size, total: spans.length })}
    </p>
    ${legible.size === 0 ? html`<p class="note">${app.t('manuscriptLocked')}</p>` : null}
    <p class="manuscript">
      ${spans.map((span) =>
        legible.has(span.id)
          ? html`<span>${span.text} </span>`
          : html`<span
              class="redacted ${redactionClass(span.text.length)}"
              aria-hidden="true"
            ></span> `,
      )}
    </p>
  </section>`
}
